import type { ModelAssetNode } from '../assets/model-asset-types'
import {
  ProjectModelDesignTreeNodeKinds,
  type ProjectModelDesignTreeNode,
  type ProjectModelDesignTreeNodeKind,
  type ProjectModelDesignTreeProjection,
  type ProjectModelDesignTreeSceneBinding,
} from './project-model-design-tree-types'

/**
 * 构建项目模型设计树投影的输入。
 */
export interface BuildProjectModelDesignTreeProjectionInput {
  /**
   * 当前项目模型资产根节点。
   */
  readonly root: ModelAssetNode
  /**
   * 场景对象绑定索引，用于把几何解析后产生的 objectId 注入设计树。
   */
  readonly sceneBindings?: readonly ProjectModelDesignTreeSceneBinding[]
}

/**
 * 构建设计树节点时使用的上下文。
 */
interface BuildProjectModelDesignTreeNodeContext {
  /**
   * 按模型节点 key 索引的场景对象 ID 列表。
   */
  readonly sceneObjectIdsByNodeKey: ReadonlyMap<string, readonly string[]>
}

/**
 * 收集模型资产树内的合法节点 key。
 *
 * @param node 当前模型资产节点。
 * @param nodeKeys 递归累积的节点 key 集合。
 */
function collectModelAssetNodeKeys(node: ModelAssetNode, nodeKeys: Set<string>): void {
  nodeKeys.add(node.nodeKey)

  for (const child of node.children) {
    collectModelAssetNodeKeys(child, nodeKeys)
  }
}

/**
 * 构建设计树场景对象索引。
 *
 * 同一个项目模型节点可能对应多个 mesh/body，因此 nodeKey 不是唯一绑定键。
 * 投影阶段只消费当前 ModelAssetNode 树中存在的绑定，其他包内部或临时绑定不阻断模型加载。
 *
 * @param root 当前模型资产根节点。
 * @param sceneBindings 场景对象绑定列表。
 * @returns 按 nodeKey 索引并保持原始顺序去重后的 objectId 列表。
 */
function buildSceneObjectIdsByNodeKey(
  root: ModelAssetNode,
  sceneBindings: readonly ProjectModelDesignTreeSceneBinding[] | undefined,
): ReadonlyMap<string, readonly string[]> {
  const nodeKeys = new Set<string>()
  const objectIdSetByNodeKey = new Map<string, Set<string>>()
  const sceneObjectIdsByNodeKey = new Map<string, readonly string[]>()

  collectModelAssetNodeKeys(root, nodeKeys)

  for (const binding of sceneBindings ?? []) {
    if (!nodeKeys.has(binding.nodeKey)) {
      continue
    }

    const objectIdSet = objectIdSetByNodeKey.get(binding.nodeKey) ?? new Set<string>()

    objectIdSet.add(binding.objectId)
    objectIdSetByNodeKey.set(binding.nodeKey, objectIdSet)
  }

  for (const [nodeKey, objectIdSet] of objectIdSetByNodeKey.entries()) {
    sceneObjectIdsByNodeKey.set(nodeKey, Array.from(objectIdSet))
  }

  return sceneObjectIdsByNodeKey
}

/**
 * 项目模型节点类型到设计树节点种类的稳定映射。
 */
const designTreeNodeKindByModelAssetNodeType = {
  assembly: ProjectModelDesignTreeNodeKinds.assemblyNode,
  part: ProjectModelDesignTreeNodeKinds.partNode,
} satisfies Record<ModelAssetNode['nodeType'], ProjectModelDesignTreeNodeKind>

/**
 * 解析模型资产节点在设计树中的业务种类。
 *
 * 适配层通常会先校验 nodeType；这里仍保留运行时断言，避免异常后端数据进入 UI 后才暴露为选择或高亮问题。
 *
 * @param node 当前项目模型资产节点。
 * @returns 与模型节点类型对应的设计树节点种类。
 * @throws 当 nodeType 无法映射时抛出包含 nodeKey 与 nodeType 的明确错误。
 */
function resolveProjectModelDesignTreeNodeKind(node: ModelAssetNode): ProjectModelDesignTreeNodeKind {
  const kindByNodeType = designTreeNodeKindByModelAssetNodeType as Readonly<Record<string, ProjectModelDesignTreeNodeKind | undefined>>
  const kind = kindByNodeType[node.nodeType]

  if (!kind) {
    throw new Error(`项目模型设计树节点类型无法识别：nodeKey=${node.nodeKey}, nodeType=${node.nodeType}`)
  }

  return kind
}

/**
 * 从项目模型节点 key 派生设计树显示名。
 *
 * @param nodeKey 项目模型节点稳定 key。
 * @returns 设计树节点显示名。
 */
function getProjectModelDesignTreeNodeLabel(nodeKey: string): string {
  const segments = nodeKey.split('/').filter((segment) => segment.length > 0)

  // 节点 key 在适配层已保证非空；这里保留兜底，避免异常后端数据让设计树 label 变成 undefined。
  return segments[segments.length - 1] ?? nodeKey
}

/**
 * 构建单个项目模型节点对应的设计树节点。
 *
 * @param node 当前项目模型资产节点。
 * @returns 与模型节点一一对应的设计树节点。
 */
function buildProjectModelDesignTreeNode(
  node: ModelAssetNode,
  context: BuildProjectModelDesignTreeNodeContext,
): ProjectModelDesignTreeNode {
  const sceneObjectIds = context.sceneObjectIdsByNodeKey.get(node.nodeKey) ?? []

  return {
    key: `project-model:${node.nodeKey}`,
    label: getProjectModelDesignTreeNodeLabel(node.nodeKey),
    kind: resolveProjectModelDesignTreeNodeKind(node),
    nodeKey: node.nodeKey,
    objectId: sceneObjectIds[0],
    objectIds: sceneObjectIds.length > 0 ? sceneObjectIds : undefined,
    children: node.children.map((child) => buildProjectModelDesignTreeNode(child, context)),
  }
}

/**
 * 将项目模型资产树投影为设计树结构。
 *
 * @param input 设计树投影构建输入。
 * @returns 可供 web-client 设计树 UI 消费的项目模型投影。
 */
export function buildProjectModelDesignTreeProjection(
  input: BuildProjectModelDesignTreeProjectionInput,
): ProjectModelDesignTreeProjection {
  const sceneObjectIdsByNodeKey = buildSceneObjectIdsByNodeKey(input.root, input.sceneBindings)

  return {
    roots: [buildProjectModelDesignTreeNode(input.root, { sceneObjectIdsByNodeKey })],
  }
}
